import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { CommonUtils } from '../../shared/utils/common.utils';
import { Resolution, ResponsivenessService } from './responsiveness.service';

@Injectable()
export class GlobalResponsivenessService {
  tablePageSize$ = new Subject<number>();
  isMobile$ = new Subject<boolean>();
  isPortraitTablet$ = new Subject<boolean>();
  isLandscapeMobile$ = new Subject<boolean>();
  isTablet$ = new Subject<boolean>();
  isDesktop$ = new Subject<boolean>();
  isDesktop2$ = new Subject<boolean>();
  isDesktop1$ = new Subject<boolean>();
  docNameLimit$ = new Subject<number>();
  spinnerMarginLeft$ = new Subject<string>();
  spinnerMarginBottom$ = new Subject<string>();
  resolutionChanged$: BehaviorSubject<boolean>;
  resolutionAttributes$: BehaviorSubject<ResolutionAttributes>;

  private currentResolution: Resolution;
  constructor(responsiveService: ResponsivenessService) {
    responsiveService.resolution.subscribe((resolution: Resolution) => {
      this.resolutionChanged$ = new BehaviorSubject(false);
      if (this.currentResolution != resolution)
        this.resolutionChanged$.next(true);
      this.currentResolution = resolution;
      this.tablePageSize$.next(this.tablePageSize);
      this.isMobile$.next(this.isMobile);
      this.isLandscapeMobile$.next(this.isLandscapeMobile);
      this.isPortraitTablet$.next(this.isPortraitTablet);
      this.isTablet$.next(this.isTablet);
      this.isDesktop$.next(this.isDesktop);
      this.isDesktop1$.next(this.isDesktop1);
      this.isDesktop2$.next(this.isDesktop2);
      this.docNameLimit$.next(this.docNameLimit);
      this.spinnerMarginLeft$.next(this.spinnerMarginLeft);
      this.spinnerMarginBottom$.next(this.spinnerMarginBottom);
      this.resolutionAttributes$ = new BehaviorSubject(
        this.getResolutionAttributes(),
      );
      this.resolutionAttributes$.next(this.getResolutionAttributes());
    });
  }

  getCurrentResolution(): Resolution {
    return this.currentResolution;
  }
  private get tablePageSize(): number {
    switch (this.currentResolution) {
      case '360x640':
      case '768x1024':
      case '1024x657':
        return null;
      case '1280x609':
        return 3;
      case '1280x913':
      case '1920x969':
        return 7;
    }
  }

  private get docNameLimit(): number {
    switch (this.currentResolution) {
      case '360x640':
      case '1280x609':
      case '1280x913':
        return 25;
      case '768x1024':
      case '1024x657':
      case '1920x969':
        return 60;
    }
  }

  private get spinnerMarginLeft(): string {
    switch (this.currentResolution) {
      case '360x640':
        return '35%';
      case '768x1024':
        return '40%';
      case '1024x657':
        return '45%';
      case '1280x609':
      case '1280x913':
      case '1920x969':
        return '50%';
    }
  }

  private get spinnerMarginBottom(): string {
    switch (this.currentResolution) {
      case '360x640':
        return '40%';
      case '768x1024':
        return '60%';
      case '1280x913':
        return '55%';
      case '1280x609':
      case '1024x657':
      case '1920x969':
        return '50%';
    }
  }

  private get isMobile(): boolean {
    if (
      this.currentResolution == '360x640' ||
      this.currentResolution == '640x360'
    )
      return true;
    return false;
  }
  private get isLandscapeMobile(): boolean {
    if (this.currentResolution == '640x360') return true;
    return false;
  }

  private get isPortraitTablet(): boolean {
    if (this.currentResolution == '768x1024') return true;
    return false;
  }

  private get isTablet(): boolean {
    if (
      this.currentResolution == '768x1024' ||
      this.currentResolution == '1024x657'
    )
      return true;
    return false;
  }
  private get isDesktop(): boolean {
    if (
      this.currentResolution == '1280x609' ||
      this.currentResolution == '1280x913' ||
      this.currentResolution == '1920x969'
    )
      return true;
    return false;
  }
  private get isDesktop1(): boolean {
    if (this.currentResolution == '1280x609') return true;
    return false;
  }
  private get isDesktop2(): boolean {
    if (this.currentResolution == '1280x913') return true;
    return false;
  }

  hideTooltips(tooltips: any) {
    tooltips.forEach(element => {
      element.hide();
    });
  }

  showTooltipGridFormat(data: any): any {
    if (data != this.formatGridText(data) && this.isDesktop) {
      return data;
    }
    return null;
  }

  formatGridText(data: string): string {
    if (this.isDesktop) {
      return CommonUtils.formatGridTextDesktop(data);
    } else if (this.isTablet) {
      return CommonUtils.formatGridTextTablet(data);
    } else {
      return CommonUtils.formatGridTextMobile(data);
    }
  }

  formatFilterSummary(summary) {
    if (this.isDesktop) {
      return summary;
    } else return CommonUtils.formatGridTextMobile(summary);
  }

  getTenderPageSize(): number {
    var startResolution = this.getCurrentResolution();
    switch (startResolution) {
      case '1280x913':
        return 7;
      case '1920x969':
        return 7;
      case '1280x609':
        return 3;
      default:
        return null;
    }
  }

  getResolutionAttributes(): ResolutionAttributes {
    var startResolution = this.getCurrentResolution();
    var attributes = new ResolutionAttributes();
    switch (startResolution) {
      case '1280x913':
        attributes.isDesktop = true;
        attributes.isDesktop2 = true;
        attributes.pageSize = 7;
        break;
      case '1920x969':
        attributes.isDesktop = true;
        attributes.pageSize = 7;

        break;
      case '1280x609':
        attributes.isDesktop = true;
        attributes.isDesktop1 = true;
        attributes.pageSize = 3;
        break;
      case '768x1024':
        attributes.isTablet = true;
        attributes.isPortraitTablet = true;
        attributes.collapsibleTable = true;
        break;
      case '1024x657':
        attributes.isTablet = true;
        attributes.isLandscapeTablet = true;
        break;
      case '360x640':
        attributes.isMobile = true;
        attributes.collapsibleTable = true;
        break;

      case '640x360':
        attributes.isMobile = true;
        attributes.isLandscapeMobile = true;
        attributes.collapsibleTable = true;
        break;

      default:
        break;
    }
    return attributes;
  }

  getIncidentOverviewSpinnerValues() {
    var startResolution = this.getCurrentResolution();
    var attributes = new SpinnerAttributes();
    attributes.marginLeft = '47%';
    attributes.marginBottom = '50%';
    switch (startResolution) {
      case '360x640':
        attributes.marginLeft = '35%';
        attributes.marginBottom = '40%';
        break;
      case '768x1024':
        attributes.marginLeft = '40%';
        break;
      case '1024x657':
        attributes.marginLeft = '45%';
        attributes.marginBottom = '40%';
        break;
      default:
        break;
    }
    return attributes;
  }

  getSelfBillOverviewSpinnerValues() {
    var startResolution = this.getCurrentResolution();
    var attributes = new SpinnerAttributes();
    attributes.marginLeft = '50%';
    attributes.marginBottom = '40%';
    switch (startResolution) {
      case '1280x913':
        attributes.marginBottom = '55%';
        break;
      case '360x640':
        attributes.marginLeft = '35%';
        attributes.marginBottom = '40%';
        break;
      case '768x1024':
        attributes.marginLeft = '40%';
        attributes.marginBottom = '50%';
        break;
      case '1024x657':
        attributes.marginLeft = '45%';
        break;
      default:
        break;
    }
    return attributes;
  }

  getOrderOverviewSpinnerValues() {
    var startResolution = this.getCurrentResolution();
    var attributes = new SpinnerAttributes();
    attributes.marginLeft = '50%';
    attributes.marginBottom = '40%';
    switch (startResolution) {
      case '360x640':
        attributes.marginLeft = '35%';
        break;
      case '768x1024':
        attributes.marginLeft = '40%';
        attributes.marginBottom = '55%';
        break;
      case '1024x657':
        attributes.marginLeft = '45%';
        break;
      case '1280x913':
        attributes.marginBottom = '55%';
        break;
      default:
        break;
    }
    return attributes;
  }
}

export class ResolutionAttributes {
  public isDesktop: boolean;
  public isDesktop2: boolean;
  public isDesktop1: boolean;
  public isTablet: boolean;
  public isPortraitTablet: boolean;
  public isLandscapeTablet: boolean;
  public isMobile: boolean;
  public isLandscapeMobile: boolean;
  public pageSize: number;
  public collapsibleTable: boolean;

  constructor() {
    this.isDesktop = false;
    this.isDesktop1 = false;
    this.isDesktop2 = false;
    this.isLandscapeTablet = false;
    this.isLandscapeMobile = false;
    this.isMobile = false;
    this.isPortraitTablet = false;
    this.isTablet = false;
    this.pageSize = null;
    this.collapsibleTable = false;
  }
}

export class SpinnerAttributes {
  public marginLeft: string;
  public marginBottom: string;

  constructor() {
    this.marginLeft = '50%';
    this.marginBottom = '50%';
  }
}
